import React, { useState } from "react";
import { Scissors, Monitor, Sparkles, Zap, Sprout, Coins, ArrowRight, BookOpen, Clock, Award, CheckCircle, HelpCircle } from "lucide-react";

const Stitch = ({ className = "", color = "#2E7D32" }) => (
  <svg className={className} viewBox="0 0 100 4" preserveAspectRatio="none">
    <line
      x1="0" y1="2" x2="100" y2="2"
      stroke={color} strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round"
    />
  </svg>
);

const programsData = [
  {
    icon: Scissors,
    title: "Tailoring & Fashion Design",
    category: "apparel",
    description: "Hands-on garment construction, pattern-making, embroidery, and boutique management training.",
    duration: "6 Months",
    level: "Beginner to Intermediate",
    eligibility: "Open to women seeking self-employment",
    outcomes: ["Run independent boutique", "Apparel industry machine operator", "Self-employed custom tailor"],
    syllabus: ["Machine handling & maintenance", "Basic & advanced stitches", "Measurements & calculations", "Dresses, blouses, and tailoring projects"],
  },
  {
    icon: Monitor,
    title: "Computer & IT Literacy",
    category: "it",
    description: "Office automation softwares, keyboard skills, web usage, digital services, and basic troubleshooting.",
    duration: "3 Months",
    level: "Beginner",
    eligibility: "Youth and students (10th standard pass/fail)",
    outcomes: ["Data entry operator", "Office assistant", "Internet cafe attendant", "E-services clerk"],
    syllabus: ["Computer fundamentals & OS", "MS Office Word, Excel & PowerPoint", "Internet safety & search", "Online banking & government e-portals"],
  },
  {
    icon: Sparkles,
    title: "Beautician & Wellness Specialist",
    category: "apparel",
    description: "Cosmetology basics, skin care, hair designing, bridal grooming, and sanitation regulations.",
    duration: "4 Months",
    level: "Beginner",
    eligibility: "Women and youth seeking beauty parlor trades",
    outcomes: ["Salon assistant", "Bridal makeup artist", "Beauty entrepreneur / parlor owner"],
    syllabus: ["Threading & facial techniques", "Hair styling & treatments", "Manicure, pedicure & waxing", "Bridal styling & product knowledge"],
  },
  {
    icon: Zap,
    title: "Electrical & House Wiring",
    category: "it",
    description: "Domestic wiring circuits, safety protocols, load calculation, and home appliance repairs.",
    duration: "6 Months",
    level: "Intermediate",
    eligibility: "Unemployed youth seeking vocational trades",
    outcomes: ["Independent domestic electrician", "Maintenance technician", "Electrical shop apprentice"],
    syllabus: ["Electrical tools usage & safety", "Single & three-phase wiring layout", "Safety fuses, earthing & MCB wiring", "Appliance motor winding & service"],
  },
  {
    icon: Sprout,
    title: "Organic Farming & Agri-Skills",
    category: "agri",
    description: "Bio-fertilizer preparation, vermicomposting, soil preservation, and crop cycle management.",
    duration: "3 Months",
    level: "Beginner",
    eligibility: "Rural farmers, youth, and self-help group members",
    outcomes: ["Organic kitchen garden advisor", "Vermicompost manufacturer", "Eco-friendly agricultural entrepreneur"],
    syllabus: ["Organic composting & Panchagavya prep", "Drip irrigation & water harvesting", "Natural pest control & soil health", "Government organic certifications process"],
  },
  {
    icon: Coins,
    title: "Entrepreneurship & SHG Finance",
    category: "agri",
    description: "Micro-business setup, bookkeeping, accounts tallying, loan compliance, and digital banking.",
    duration: "2 Months",
    level: "All Levels",
    eligibility: "Self Help Group (SHG) members & prospective micro-entrepreneurs",
    outcomes: ["Self Help Group bookkeeper", "Micro-business entrepreneur", "Local retail shop manager"],
    syllabus: ["SHG record maintenance", "Double-entry bookkeeping basics", "Cost calculation & profit margins", "Applying for bank loans & government subsidies"],
  },
];

const faqs = [
  {
    q: "Who is eligible to join these vocational courses?",
    a: "Our courses are specifically designed for rural youth, women, tribal populations, and individuals from economically disadvantaged backgrounds. Minimum age is 15 years, and educational requirements vary from basic literacy to 10th standard passes, depending on the course.",
  },
  {
    q: "Are the training programs free?",
    a: "Yes, as a registered charitable public trust, MB Foundation runs its vocational training programs 100% voluntary and free of cost (or at nominal registration fees) for target underprivileged groups. Contact our office or submit an inquiry to verify availability.",
  },
  {
    q: "Will I get a certificate after completing the training?",
    a: "Yes, all students who maintain at least 80% attendance and pass the practical and theory final assessments are awarded a course completion certificate from MB Foundation.",
  },
  {
    q: "Where are the training classes held?",
    a: "The primary courses are conducted at our registered training center in Vellore, Tamil Nadu. Mobile training camps are also organized in rural neighborhoods and tribal blocks throughout the district depending on ongoing developmental projects.",
  },
];

const ProgramsPage = () => {
  const [activeTab, setActiveTab] = useState("all");

  const filteredPrograms = activeTab === "all"
    ? programsData
    : programsData.filter((p) => p.category === activeTab);

  return (
    <div className="bg-[#FBF9F3] min-h-screen pt-[84px] pb-16">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=IBM+Plex+Sans:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@500&display=swap');
        .mbf-display { font-family: 'Fraunces', serif; }
        .mbf-body { font-family: 'IBM Plex Sans', sans-serif; }
        .mbf-mono { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      {/* Standalone Page Hero */}
      <section className="bg-[#082440] text-white py-20 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, #fff 0 1px, transparent 1px 34px), repeating-linear-gradient(90deg, #fff 0 1px, transparent 1px 34px)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <span className="mbf-mono text-[12px] tracking-[0.2em] uppercase text-[#E0A526]">
            Vocational Skills
          </span>
          <h1 className="mbf-display text-4xl sm:text-5xl font-semibold mt-4 tracking-tight">
            Our Training Programs
          </h1>
          <Stitch className="w-32 h-2 mx-auto mt-6" color="#E0A526" />
          <p className="mbf-body mt-6 text-blue-100/70 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Practical apprenticeships that transform local potential into self-reliant, sustainable livelihoods.
          </p>
        </div>
      </section>

      {/* Program Tabs & Cards List */}
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-8">
        
        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {[
            { id: "all", label: "All Programs" },
            { id: "apparel", label: "Apparel & Designing" },
            { id: "it", label: "Computers & Technical" },
            { id: "agri", label: "Eco-Agri & Finance" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`mbf-body px-5 py-2.5 text-xs font-semibold tracking-wider uppercase transition border ${
                activeTab === tab.id
                  ? "bg-[#0A355C] border-[#0A355C] text-white"
                  : "bg-white border-[#0A355C]/15 text-[#0A355C] hover:bg-[#F2ECDD]/40"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Detailed Program Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {filteredPrograms.map((prog, index) => {
            const Icon = prog.icon;

            return (
              <div
                key={index}
                className="bg-white border border-[#0A355C]/10 p-6 md:p-8 flex flex-col justify-between hover:shadow-lg transition duration-300 relative overflow-hidden"
              >
                {/* corner stitch ornament */}
                <span className="absolute top-0 right-0 w-3 h-3 bg-[#E0A526]" style={{ clipPath: "polygon(100% 0, 0 0, 100% 100%)" }} />

                <div>
                  {/* Top line with duration */}
                  <div className="flex justify-between items-start gap-4">
                    <div className="w-12 h-12 rounded-full border border-[#0A355C] flex items-center justify-center text-[#2E7D32] bg-[#FBF9F3]">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-right">
                      <div className="mbf-mono text-[10.5px] uppercase tracking-wider text-[#2E7D32]/80 bg-[#F2ECDD]/50 px-2.5 py-1 font-semibold rounded-none inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {prog.duration}
                      </div>
                      <div className="mbf-mono text-[9px] uppercase tracking-widest text-[#6b6355] mt-1 block">
                        {prog.level}
                      </div>
                    </div>
                  </div>

                  {/* Program Title */}
                  <h3 className="mbf-display text-2xl font-semibold text-[#0A355C] mt-6">
                    {prog.title}
                  </h3>
                  <p className="mbf-body text-[#514a3d] text-[14px] mt-2.5 leading-relaxed">
                    {prog.description}
                  </p>

                  <Stitch className="w-16 h-2 my-5" color="#2E7D32" />

                  {/* Quick details */}
                  <div className="grid sm:grid-cols-2 gap-6 mt-4">
                    <div>
                      <h4 className="mbf-mono text-[11px] uppercase tracking-wider text-[#0A355C]/60 flex items-center gap-1">
                        <BookOpen className="w-3.5 h-3.5 text-[#E0A526]" /> Course Syllabus
                      </h4>
                      <ul className="mbf-body text-xs text-[#6b6355] space-y-1.5 mt-2.5 pl-4 list-disc">
                        {prog.syllabus.map((syl, i) => (
                          <li key={i}>{syl}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="mbf-mono text-[11px] uppercase tracking-wider text-[#0A355C]/60 flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5 text-[#2E7D32]" /> Key Outcomes
                      </h4>
                      <ul className="mbf-body text-xs text-[#6b6355] space-y-1.5 mt-2.5 pl-4 list-disc">
                        {prog.outcomes.map((out, i) => (
                          <li key={i}>{out}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Footer details & CTA */}
                <div className="border-t border-[#0A355C]/10 mt-8 pt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="text-xs mbf-body text-[#6b6355]">
                    <span className="font-semibold text-[#0A355C] block sm:inline">Eligibility:</span> {prog.eligibility}
                  </div>
                  <a
                    href="#contact"
                    className="mbf-body self-start sm:self-center inline-flex items-center gap-1.5 bg-[#0A355C] hover:bg-[#082440] text-white px-4 py-2.5 font-semibold text-xs uppercase tracking-wider transition shadow-sm"
                  >
                    Inquire Course
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Program FAQs Section */}
      <section className="bg-[#F2ECDD] py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="mbf-mono text-[12px] tracking-[0.18em] uppercase text-[#2E7D32]">
              Got Questions?
            </span>
            <h2 className="mbf-display mt-3 text-3xl font-semibold text-[#0A355C]">
              Frequently Asked Questions
            </h2>
            <Stitch className="w-24 h-2 mx-auto mt-4" color="#2E7D32" />
          </div>

          <div className="space-y-6">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-[#0A355C]/10 p-6 md:p-8 flex gap-4 items-start">
                <HelpCircle className="w-6 h-6 text-[#E0A526] shrink-0 mt-0.5" />
                <div>
                  <h4 className="mbf-display text-lg font-semibold text-[#0A355C]">
                    {faq.q}
                  </h4>
                  <p className="mbf-body text-[14px] text-[#514a3d] mt-2 leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramsPage;
